import React, { HTMLAttributes, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { DialogActions } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import ControlledAutocomplete, { AutocompleteOption } from '../ControlledAutocomplete/ControlledAutocomplete';
import BaseDialog from '../../../common/BaseDialog/BaseDialog';
import styles from './ContentConnectDialog.module.scss';
import BaseDialogHeading from '../../../common/BaseDialog/BaseDialogHeading';
import { useConnectFile, useContentSearch } from '../../../../lib/api/scormApi';
import Button from '../../../common/Button/Button';
import { COMMON_LABELS, SCORM_LABELS } from '../../../../lib/constants/labelKeys';
import { Label } from '../../../common/Label/Label';
import useHandleAction from '../../../../lib/hooks/useHandleAction';
import Loader from '../../../common/Loader/Loader';
import Notification, { NotificationType } from '../Notification/Notification';
import { DEFAULT_DEBOUNCE_TIME } from '../../../../lib/constants/globals';
import useDebouncedCallback from '../../../../lib/hooks/useDebouncedCallback';
import { classes } from '../../../../lib/utils/cssUtils';
import { isNil } from '../../../../lib/utils/isNil';
import { Api } from '../../../../lib/types/apiSpec';
import useErrorMapping from '../../../../lib/hooks/useErrorMapping';

export type ContentConnectDialogProps = {
  onClose: () => void;
  moduleId: string;
  successMessage?: string;
  initialValue?: string;
};

type ConnectAutocompleteOption = {
  name: string;
  connection?: Api.ScormConnection;
  location?: string;
};

const renderOption = (props: HTMLAttributes<HTMLLIElement>, option: AutocompleteOption<ConnectAutocompleteOption>) => {
  const { className, ...rest } = props;

  return (
    <li {...rest} className={classes(className, { [styles.connectedLabel]: !!option.label.connection })}>
      {option.label.name}
    </li>
  );
};

const AutocompleteSkeleton = ({ isLoading, children }: PropsWithChildren<{ isLoading?: boolean }>) =>
  isLoading ? <Skeleton className={styles.autocompleteSkeleton} /> : children;

const ContentConnectDialog = ({
  onClose,
  moduleId,
  successMessage = '',
  initialValue = '',
}: ContentConnectDialogProps) => {
  const [inputValue, setInputValue] = useState('');
  const [valueToSearch, setValueToSearch] = useState('');
  const { data, error, isValidating, isLoading } = useContentSearch(valueToSearch);
  const [fileSelected, setFileSelected] = useState<AutocompleteOption<ConnectAutocompleteOption> | null>(
    initialValue ? { id: initialValue, label: { name: '' } } : null,
  );
  const connectFile = useConnectFile();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { getParsedErrorMessage } = useErrorMapping();

  const { handleAction, isPending } = useHandleAction({
    action: connectFile,
    onSuccess: onClose,
    successMessage,
    customOnError: setErrorMessage,
  });

  const handleConfirm = useCallback(
    async () => handleAction({ id: moduleId, fileId: fileSelected?.id ?? '' }),
    [handleAction, fileSelected],
  );

  const setDebouncedValueToSearch = useDebouncedCallback(
    (value: string) => setValueToSearch(value),
    DEFAULT_DEBOUNCE_TIME,
  );

  const handleSetInputValue = (value: string) => {
    setInputValue(value);
    setDebouncedValueToSearch(value);
  };

  const files = useMemo(
    () => data?.map(({ id, name, connection, location }) => ({ label: { name, connection, location }, id })) ?? [],
    [data],
  );

  useEffect(() => {
    if (errorMessage) {
      setErrorMessage(null);
    }
  }, [inputValue]);

  useEffect(() => {
    if (files && fileSelected && !fileSelected.label.name) {
      const file = files.find(({ id }) => id.toString() === fileSelected.id);

      if (file) {
        setFileSelected(file);
      }
    }
  }, [files]);

  return (
    <BaseDialog classes={{ paper: styles.dialog }} open onClose={onClose}>
      <BaseDialogHeading className={styles.titleRow} closeButtonProps={{ onClick: onClose }}>
        <Label name={SCORM_LABELS.CONNECT_DIALOG.TITLE} />
      </BaseDialogHeading>
      <AutocompleteSkeleton isLoading={isLoading && isNil(data)}>
        {!error && (
          <ControlledAutocomplete
            disabled={isPending}
            isLoading={isValidating}
            options={files}
            value={fileSelected}
            inputValue={inputValue}
            onChange={handleSetInputValue}
            onOptionChange={setFileSelected}
            placeholderKey={COMMON_LABELS.SEARCH.FILE}
            renderOption={renderOption}
            getOptionLabel={({ label }) => label.name}
            groupBy={file => file.label.location ?? ''}
          />
        )}
      </AutocompleteSkeleton>
      {(!!errorMessage || !!error) && (
        <Notification
          content={errorMessage ?? getParsedErrorMessage(error)}
          type={NotificationType.Error}
          showCloseButton={false}
          transitionAnimation
          open
          ariaRole="alert"
          wrapperClass={styles.errorsContainer}
        />
      )}
      {!error && (
        <DialogActions className={styles.dialogActions}>
          <Button variant="outlined" onClick={onClose}>
            <Label name={COMMON_LABELS.DIALOG.CANCEL} />
          </Button>
          {isPending ? (
            <Loader containerProps={{ className: styles.loader }} />
          ) : (
            <Button
              disabled={!fileSelected || !!errorMessage || fileSelected.id.toString() === initialValue}
              onClick={handleConfirm}
            >
              <Label name={SCORM_LABELS.CONNECT_DIALOG.CONFIRM_BUTTON} />
            </Button>
          )}
        </DialogActions>
      )}
    </BaseDialog>
  );
};

export default ContentConnectDialog;
